import type { ArtworkSet, LandingMedia } from "@pax/api-contract/presets/v1alpha1";
import { Button, cn } from "@pax/ui";
import { useState } from "react";
import { LandingArtwork } from "@/features/presets/components/LandingArtwork";

export type EntryPhase = "connecting" | "drawing" | "retrying" | "failed" | "closed";

/**
 * Two phases rather than one per step of the handshake. Connecting covers
 * everything from waking the game server to replaying the story, which is the
 * long part; drawing covers the map. Naming each step underneath would put
 * four lines on screen in the time it takes to read one.
 */
const PHASE_LABELS: Record<EntryPhase, string> = {
  connecting: "Connecting to the game",
  drawing: "Loading the map",
  retrying: "Still connecting",
  failed: "Couldn't open this game",
  closed: "This game has ended",
};

/**
 * What a player looks at between opening a game and the map surfacing.
 *
 * One screen for the whole wait. The map, the story, and the controls mount
 * behind it as they become ready, so the curtain lifting is the only visible
 * change between the games list and a playable world.
 */
export function EntryCurtain({
  phase,
  title,
  perspectiveName,
  cover,
  landing,
  error,
  lifting = false,
  onLifted,
  onRetry,
}: {
  phase: EntryPhase;
  /** Why the last attempt failed, shown under the phase while retrying or once it has failed. */
  error?: string | null;
  title?: string;
  perspectiveName?: string | null;
  cover?: ArtworkSet | null;
  landing?: LandingMedia | null;
  /** Start the fade. The map surfaces through the curtain, not after it. */
  lifting?: boolean;
  onLifted?: () => void;
  onRetry?: () => void;
}) {
  const [artworkLoaded, setArtworkLoaded] = useState(false);

  return (
    <div
      aria-busy={!lifting}
      onTransitionEnd={event => {
        if (lifting && event.target === event.currentTarget) onLifted?.();
      }}
      className={cn(
        "absolute inset-0 z-20 overflow-hidden bg-black",
        // Long, because it runs against the map's own reveal rather than
        // before it: the artwork dissolves while the water is still moving.
        "transition-opacity duration-1000 ease-out motion-reduce:duration-200",
        lifting && "pointer-events-none opacity-0",
      )}
    >
      {(landing || cover) && (
        <div
          className={cn(
            "cover-drift absolute inset-0 size-full transition-opacity duration-700",
            artworkLoaded ? "opacity-100" : "opacity-0",
          )}
        >
          <LandingArtwork
            landing={landing}
            fallback={cover}
            priority
            onReady={() => setArtworkLoaded(true)}
            className="size-full"
          />
        </div>
      )}

      {/* Dark enough that the map arrives as the lights coming up rather than
          as a swap, and that the title needs no plate behind it. */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/40" />

      <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-3 p-8 sm:p-12">
        {title && (
          <div className="animate-in fade-in space-y-1.5 duration-700">
            {perspectiveName && (
              <p className="text-xs font-medium uppercase tracking-widest text-white/50">
                {perspectiveName}
              </p>
            )}
            <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {title}
            </h1>
          </div>
        )}

        <div className="flex items-center gap-3">
          <p
            className={cn(
              "text-sm",
              phase === "closed" && "text-white/60",
              phase === "failed" && "text-white",
              phase !== "closed" && phase !== "failed" && "shimmer-text shimmer-text-light",
            )}
          >
            {PHASE_LABELS[phase]}
          </p>

          {/* Outlined in the artwork's own white rather than the theme's button
              colours, which would land as a filled chip dropped on the art. */}
          {onRetry && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRetry}
              className="h-7 border border-white/25 px-2.5 text-white hover:bg-white/15 hover:text-white"
            >
              Try again
            </Button>
          )}
        </div>

        {error && phase !== "drawing" && (
          <p className="max-w-md text-pretty text-sm text-white/60">{error}</p>
        )}
      </div>
    </div>
  );
}
